import styled from 'styled-components';
import { FunctionComponent, MouseEvent, useEffect, useState } from 'react';
import { Button } from '@primer/react';
import logo from '../../images/cjr-logo-playonline.svg';
import { AccountMenu, CHANGE_MENU_WIDTH } from '../../basicComponents/accountMenu/AccountMenu';
import { useWindowDimensions } from '../../hooks/useWindowDimensions';
import {
  MY_GAMES_PATH,
  REGISTRATION_PATH,
  PLANS_PATH,
  LOGIN_PATH,
  ON_BOARDING_PATH,
  BILLING_PATH,
} from '../../utils/constants';
import { selectAuth } from '../../redux/slices/auth.slice';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setMenu } from '../../redux/slices/menu.slice';
import { useNavigate } from 'react-router-dom';
import { MoreMenu } from './MoreMenu';
import { useGetSubscriptionStatusQuery } from '../../redux/apis/payment';

// Mirrors the cozyjuicyreal.com header (cozyjuicyreal/src/components/layout/SiteHeader.tsx).
const Header = styled('header')<{ $scrolled: boolean }>`
  position: sticky;
  top: 0;
  z-index: 10;
  width: 100%;
  margin-bottom: 50px;
  background: hsla(40, 40%, 99%, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid
    ${({ $scrolled }) => ($scrolled ? 'hsla(252, 30%, 90%, 0.6)' : 'transparent')};
  box-shadow: ${({ $scrolled }) => ($scrolled ? 'var(--shadow-soft)' : 'none')};
  transition: border-color 150ms, box-shadow 150ms;
`;

const Inner = styled('div')<{ $compact: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  max-width: 1320px;
  height: ${({ $compact }) => ($compact ? '64px' : '80px')};
  margin: 0 auto;
  padding: 0 ${({ $compact }) => ($compact ? '20px' : '40px')};
`;

const Left = styled('div')`
  display: flex;
  align-items: center;
  gap: 24px;
  min-width: 0;
`;

const Logo = styled('a')`
  display: flex;
  flex-shrink: 0;

  img {
    height: 42px; /* keeps the wordmark at 40px; the larger badge adds the rest */
    width: auto;
  }
`;

const Nav = styled('nav')`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const NavItem = styled('button')<{ $selected?: boolean }>`
  white-space: nowrap;
  padding: 8px 12px;
  border: 0;
  border-radius: 9999px;
  font: 500 14px var(--font-body);
  color: ${({ $selected }) => ($selected ? 'var(--primary)' : 'hsla(263, 50%, 12%, 0.75)')};
  background: ${({ $selected }) => ($selected ? 'var(--secondary)' : 'transparent')};
  cursor: pointer;
  transition: color 150ms, background-color 150ms;

  &:hover {
    color: var(--primary);
    background: ${({ $selected }) =>
      $selected ? 'var(--secondary)' : 'hsla(252, 100%, 96%, 0.6)'};
  }
`;

const Actions = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

// MoreMenu positions itself 40px below the top of its container; this
// lines its pointer up with the bottom of the nav item.
const MoreMenuAnchor = styled('div')`
  position: absolute;
  top: 18px;
  left: 0;
`;

export const TopMenu: FunctionComponent = ({}) => {
  const navigate = useNavigate();
  const { width } = useWindowDimensions();
  const { isLoggedIn, isGuest } = useAppSelector(selectAuth);
  const [moreBallon, setMoreBallon] = useState(false);
  const [leftPositionMoreMenu, setLeftPositionMoreMenu] = useState<number>(0);
  const [scrolled, setScrolled] = useState(false);
  const sub = useGetSubscriptionStatusQuery(undefined, {
    skip: !isLoggedIn || isGuest,
  });
  const dispatch = useAppDispatch();

  const isWide = width > CHANGE_MENU_WIDTH;
  const isMember = isLoggedIn && !isGuest;
  const hasPaidPlan = sub.data?.find(
    (s) => s.isSubscriptionActive && !s.isTemporaryAddOnSubscription && s.plan !== 'FREE'
  );

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const openMoreMenu = (event: MouseEvent<HTMLButtonElement>) => {
    setLeftPositionMoreMenu(event.currentTarget.getBoundingClientRect().left);
    setMoreBallon(true);
  };

  return (
    <Header $scrolled={scrolled}>
      <Inner $compact={!isWide}>
        <Left>
          <Logo
            aria-label="Cozy Juicy Real home"
            href="/"
            onClick={(event) => {
              event.preventDefault();
              navigate('/');
            }}
          >
            <img alt="Cozy Juicy Real" src={logo} />
          </Logo>
          {isWide ? (
            <Nav>
              {isMember ? (
                <NavItem
                  $selected={window.location.pathname == MY_GAMES_PATH}
                  onClick={() => {
                    navigate(MY_GAMES_PATH);
                    dispatch(setMenu('myGames'));
                  }}
                >
                  My Games
                </NavItem>
              ) : null}
              <NavItem
                $selected={window.location.pathname == ON_BOARDING_PATH}
                onClick={() => {
                  if (!isMember) return dispatch(setMenu('howToPlay'));
                  navigate(ON_BOARDING_PATH);
                  dispatch(setMenu('onBoarding'));
                }}
              >
                {isMember ? 'How to' : 'Game rules'}
              </NavItem>
              <NavItem
                $selected={window.location.pathname == PLANS_PATH}
                onClick={() => {
                  if (isMember && hasPaidPlan) {
                    navigate(BILLING_PATH + '#add-ons');
                  } else {
                    navigate(PLANS_PATH);
                  }
                }}
              >
                {isMember ? (hasPaidPlan ? 'Add-ons' : 'Upgrade') : 'Plans & Pricing'}
              </NavItem>
              <NavItem $selected={moreBallon} onClick={openMoreMenu}>
                More...
              </NavItem>
            </Nav>
          ) : null}
        </Left>

        {isMember || !isWide ? (
          <AccountMenu />
        ) : (
          <Actions>
            {/* Guests are already signed in to a game, so logging in from here would
                drop them from it; they only get the sign-up button. */}
            {!isLoggedIn ? (
              // Plain link like the website's "Book a demo" in its menu bar.
              <NavItem onClick={() => navigate(LOGIN_PATH)} type="button">
                Login
              </NavItem>
            ) : null}
            {window.location.pathname != REGISTRATION_PATH ? (
              <Button
                onClick={() =>
                  // Guests are in a game, so sign-up opens in a new tab to keep it running.
                  isGuest
                    ? window.open(
                        `${window.location.origin}${REGISTRATION_PATH}`,
                        '_blank',
                        'noreferrer'
                      )
                    : navigate(REGISTRATION_PATH)
                }
                type="button"
                variant="primary"
              >
                Sign up free
              </Button>
            ) : null}
          </Actions>
        )}
      </Inner>
      {moreBallon ? (
        <MoreMenuAnchor>
          <MoreMenu
            leftPosition={leftPositionMoreMenu - 35}
            setClose={() => setMoreBallon(false)}
          />
        </MoreMenuAnchor>
      ) : null}
    </Header>
  );
};
